import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid, parseISO, startOfToday } from "date-fns";
import { useDispatch } from "react-redux";
import { Fonts } from "../../constants/AppEnums";
import AppDialog from "../../components/AppBase/AppDialog";
import AppConfirmDialog from "../../components/AppBase/AppConfirmDialog";
import { showMessage } from "../../redux/actions/Common";
import { removeUserFromRisOrg, updateRisUser } from "../../redux/actions/RisAdmin";
import { theme } from "../../themes/theme";
import { RisOrg } from "../../types/models/RisOrg";
import { RisOrgUser, RisUpdateUserRequest } from "../../types/models/RisOrgUser";
import {
  AssignableAccessLevel,
  Caller,
  accessLevelLabels,
  assignableAccessLevels,
  canEditName,
  canManageUser,
  isKiwiRailOrg,
  isSelf,
  toAssignableAccessLevel,
} from "./permissions";

const MAX_NAME_LENGTH = 50;

const toDate = (value?: string | null) => {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

const toDateString = (value: Date | null) =>
  value && isValid(value) ? format(value, "yyyy-MM-dd") : null;

interface EditUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the updated user after a change is saved */
  onChanged: (user: RisOrgUser) => void;
  user?: RisOrgUser;
  caller: Caller;
  /** The orgs the caller can see, to tell whether the user is in the KiwiRail org */
  orgs: RisOrg[];
}

/**
 * Edit a user's name, expiry date and access level, or deactivate them or remove them from their
 * org. Only the actions the caller is allowed are shown.
 */
const EditUserDialog: React.FC<EditUserDialogProps> = ({
  isOpen,
  onClose,
  onChanged,
  user,
  caller,
  orgs,
}) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [accessLevel, setAccessLevel] = useState<AssignableAccessLevel>("User");
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [confirmAction, setConfirmAction] = useState<"deactivate" | "remove">();

  useEffect(() => {
    if (isOpen && user) {
      setFirstName(user.FirstName ?? "");
      setLastName(user.LastName ?? "");
      setExpiryDate(toDate(user.ExpiryDate));
      setAccessLevel(toAssignableAccessLevel(user.AccessLevel));
      setErrors({});
      setConfirmAction(undefined);
    }
  }, [isOpen, user]);

  if (!user) return null;

  const self = isSelf(caller, user);
  const editableName = user.Active && canEditName(caller, user);
  const manageable = user.Active && canManageUser(caller, user);
  const levelOptions = assignableAccessLevels(
    caller,
    isKiwiRailOrg(orgs.find((org) => org.Id === user.OrgId))
  );
  const currentLevel = toAssignableAccessLevel(user.AccessLevel);
  // Keep the current level selectable even if the caller couldn't assign it
  const accessLevelChoices = levelOptions.includes(currentLevel)
    ? levelOptions
    : [currentLevel, ...levelOptions];

  const buildChanges = () => {
    const changes: RisUpdateUserRequest = {};
    if (editableName) {
      const newFirst = firstName.trim();
      const newLast = lastName.trim();
      if (newFirst !== (user.FirstName ?? "") || newLast !== (user.LastName ?? "")) {
        changes.FirstName = newFirst;
        changes.LastName = newLast || null;
      }
    }
    if (manageable) {
      const newExpiry = toDateString(expiryDate);
      if (newExpiry !== (user.ExpiryDate ?? null)) {
        changes.ExpiryDate = newExpiry;
      }
      if (accessLevel !== currentLevel) {
        changes.AccessLevel = accessLevel;
      }
    }
    return changes;
  };

  const validate = () => {
    const newErrors: Record<string, string | undefined> = {};
    if (editableName) {
      if (firstName.trim().length === 0) newErrors.firstName = "Please enter a first name";
      else if (firstName.trim().length > MAX_NAME_LENGTH)
        newErrors.firstName = `Must be ${MAX_NAME_LENGTH} characters or fewer`;
      if (lastName.trim().length > MAX_NAME_LENGTH)
        newErrors.lastName = `Must be ${MAX_NAME_LENGTH} characters or fewer`;
    }
    if (manageable && expiryDate) {
      if (!isValid(expiryDate)) newErrors.expiryDate = "Please enter a valid date";
      else if (expiryDate < startOfToday()) newErrors.expiryDate = "Can't be in the past";
    }
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const finish = (updated: RisOrgUser | undefined, message: string) => {
    setIsSubmitting(false);
    if (updated) {
      dispatch(showMessage(message));
      onChanged(updated);
    }
  };

  const handleSave = () => {
    if (!validate()) return;
    const changes = buildChanges();
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }
    setIsSubmitting(true);
    dispatch(
      updateRisUser(user.Id, changes, (updated) => finish(updated, "The user has been updated."))
    );
  };

  const handleDeactivate = () => {
    setConfirmAction(undefined);
    setIsSubmitting(true);
    dispatch(
      updateRisUser(user.Id, { Active: false }, (updated) =>
        finish(updated, `${user.Email} has been deactivated.`)
      )
    );
  };

  const handleRemove = () => {
    setConfirmAction(undefined);
    if (!user.OrgId) return;
    setIsSubmitting(true);
    dispatch(
      removeUserFromRisOrg(user.OrgId, user.Id, (updated) =>
        finish(
          updated,
          `${user.Email} has been removed from ${user.OrgName ?? "the organisation"}.`
        )
      )
    );
  };

  const readOnlyReason = !user.Active
    ? "This user has been deactivated."
    : !editableName
      ? "You can't make changes to this user."
      : self
        ? "You can change your own name. Ask an Admin or Org Manager to change anything else."
        : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppDialog
        open={isOpen}
        dividers
        disableBackDropOffClose
        title={
          <Box>
            <Typography sx={{ fontWeight: Fonts.SEMI_BOLD }}>Edit User</Typography>
            <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
              {user.Email}
              {user.OrgName ? ` · ${user.OrgName}` : ""}
            </Typography>
          </Box>
        }
        onClose={onClose}
      >
        <Box sx={{ position: "relative" }}>
          {isSubmitting && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1,
              }}
            >
              <CircularProgress size="3rem" sx={{ color: theme.palette.primary.main }} />
            </Box>
          )}
          {readOnlyReason && (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="info">{readOnlyReason}</Alert>
            </Box>
          )}
          <Stack direction="row" spacing={2} sx={{ px: 2, py: 1 }}>
            <TextField
              required
              fullWidth
              id="EditUserFirstName"
              label="First name"
              value={firstName}
              disabled={!editableName}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setFirstName(event.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <TextField
              fullWidth
              id="EditUserLastName"
              label="Last name"
              value={lastName}
              disabled={!editableName}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setLastName(event.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ px: 2, py: 2 }}>
            <DatePicker
              label="Expiry date"
              value={expiryDate}
              onChange={(value) => setExpiryDate(value)}
              format="dd/MM/yyyy"
              minDate={startOfToday()}
              disabled={!manageable}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.expiryDate,
                  helperText:
                    errors.expiryDate ??
                    (manageable ? "Leave empty if the user doesn't expire" : undefined),
                },
                actionBar: { actions: ["clear", "cancel", "accept"] },
              }}
            />
            <FormControl fullWidth disabled={!manageable}>
              <InputLabel id="edit-user-access-level-label">Access level</InputLabel>
              <Select
                labelId="edit-user-access-level-label"
                label="Access level"
                value={accessLevel}
                onChange={(event) => setAccessLevel(event.target.value as AssignableAccessLevel)}
              >
                {accessLevelChoices.map((level) => (
                  <MenuItem key={level} value={level} disabled={!levelOptions.includes(level)}>
                    {accessLevelLabels[level]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {manageable && accessLevel !== currentLevel && (
            <Box sx={{ px: 2 }}>
              <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                Access level changes take effect the next time the user signs in.
              </Typography>
            </Box>
          )}
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-between", alignItems: "center", my: 2, px: 2 }}
          >
            <Stack direction="row" spacing={1}>
              {manageable && (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => setConfirmAction("deactivate")}
                  sx={{ textTransform: "none" }}
                >
                  Deactivate
                </Button>
              )}
              {canManageUser(caller, user) && user.OrgId && (
                <Button
                  color="error"
                  variant="text"
                  disabled={isSubmitting}
                  onClick={() => setConfirmAction("remove")}
                  sx={{ textTransform: "none" }}
                >
                  Remove from organisation
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              {editableName && (
                <Button
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={handleSave}
                  sx={{ minWidth: 100, color: "white", textTransform: "none" }}
                >
                  Save Changes
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ minWidth: 100, textTransform: "none" }}
              >
                {editableName ? "Cancel" : "Close"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </AppDialog>
      <AppConfirmDialog
        open={confirmAction === "deactivate"}
        onDeny={() => setConfirmAction(undefined)}
        onConfirm={handleDeactivate}
        dialogTitle="Deactivate user?"
        title={`${user.Email} won't be able to sign in. This can't be undone here.`}
        confirmButtonTitle="Deactivate"
        denyButtonTitle="Cancel"
      />
      <AppConfirmDialog
        open={confirmAction === "remove"}
        onDeny={() => setConfirmAction(undefined)}
        onConfirm={handleRemove}
        dialogTitle="Remove from organisation?"
        title={`Remove ${user.Email} from ${user.OrgName ?? "their organisation"}?${
          user.AccessLevel === "Orgmanager"
            ? " They'll also lose Org Manager access."
            : user.AccessLevel === "Admin"
              ? " They'll also lose Admin access."
              : ""
        }`}
        confirmButtonTitle="Remove"
        denyButtonTitle="Cancel"
      />
    </LocalizationProvider>
  );
};

export default EditUserDialog;
